import { useTranslation } from "@next-platform/client-common-core";
import { Modal } from "@next-platform/client-common-spicy-ui";
import { JSX, useCallback, useEffect, useState } from "react";

import { Row } from "@/components/customComponents";
import SpicyButton from "@/components/customComponents/SpicyButton";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import PreventManageSubscriptionModal from "@/components/PreventManageSubscriptionModal";
import { DELETE_ACCOUNT_AFTER_SECONDS } from "@/constants";

const DELETE_ACCOUNT_AFTER_SECONDS_INCREMENTED = DELETE_ACCOUNT_AFTER_SECONDS + 1;
const DELETE_COUNTDOWN_INTERVAL_MS = 1000;

type ResolvePrimaryActionButtonPropsParams = {
  isManageLinkStep: boolean;
  isManageMobileStep: boolean;
  manageLinkHref?: string;
  isExternalManageLink: boolean;
  onOpenPreventModal: () => void;
  onCancel: () => void;
};

const resolvePrimaryActionButtonProps = ({
  isManageLinkStep,
  isManageMobileStep,
  manageLinkHref,
  isExternalManageLink,
  onOpenPreventModal,
  onCancel,
}: ResolvePrimaryActionButtonPropsParams) => {
  if (isManageLinkStep) {
    return {
      as: LocalizedLink,
      to: manageLinkHref,
      target: isExternalManageLink ? "_blank" : undefined,
      rel: isExternalManageLink ? "noopener noreferrer" : undefined,
      "data-testid": "AccountDeleteModal-ManageSubscriptionLink",
    };
  }

  if (isManageMobileStep) {
    return {
      onPress: onOpenPreventModal,
      "data-testid": "AccountDeleteModal-ManageSubscriptionButton",
    };
  }

  return {
    onPress: onCancel,
  };
};

type AccountDeleteModalProps = {
  isVisible: boolean;
  onClose: () => void;
  isLoading: boolean;
  onConfirmation: () => Promise<void>;
  isManageSubscription: boolean;
  isMobileManagedSubscription?: boolean;
  manageLinkHref?: string;
  isExternalManageLink?: boolean;
};

export default function AccountDeleteModal({
  isVisible,
  onClose,
  isLoading,
  onConfirmation,
  isManageSubscription,
  isMobileManagedSubscription = false,
  manageLinkHref,
  isExternalManageLink = false,
}: AccountDeleteModalProps): JSX.Element {
  const { t } = useTranslation();
  const warningContent = {
    TITLE: t("user:profile.modal.deleteAccount.withSubscription.title"),
    DESCRIPTION: t("user:profile.modal.deleteAccount.withSubscription.description"),
    CANCEL_BUTTON_TITLE: t("user:profile.modal.deleteAccount.withSubscription.cta.manageSubscription"),
    ACTION_BUTTON_TITLE: t("user:profile.modal.deleteAccount.withSubscription.cta.proceed"),
  };
  const deletionContent = {
    TITLE: t("user:profile.modal.deleteAccount.noSubscription.title"),
    DESCRIPTION: t("user:profile.modal.deleteAccount.noSubscription.description"),
    CANCEL_BUTTON_TITLE: t("common:cancel"),
    ACTION_BUTTON_TITLE: t("user:profile.modal.deleteAccount.noSubscription.cta.removeAccount"),
  };
  const [stateIsWarnAboutSubscription, setStateIsWarnAboutSubscription] = useState(isManageSubscription);
  const [stateIsPreventModalOpen, setStateIsPreventModalOpen] = useState(false);
  const [timeoutCounterId, setTimeoutCounterId] = useState<ReturnType<typeof setInterval> | undefined>(undefined);
  const [remainingSeconds, setRemainingSeconds] = useState(DELETE_ACCOUNT_AFTER_SECONDS_INCREMENTED);

  const shouldShowRemainingSeconds = remainingSeconds < DELETE_ACCOUNT_AFTER_SECONDS_INCREMENTED && remainingSeconds > 0;

  const handleOpenPreventModal = useCallback(() => {
    setStateIsPreventModalOpen(true);
  }, []);

  const handleClosePreventModal = useCallback(() => {
    setStateIsPreventModalOpen(false);
  }, []);

  const handleCancel = () => {
    if (isLoading) {
      return;
    }
    if (isManageSubscription) {
      setStateIsWarnAboutSubscription(true);
    }
    clearInterval(timeoutCounterId);
    setRemainingSeconds(DELETE_ACCOUNT_AFTER_SECONDS_INCREMENTED);
    onClose();
  };

  useEffect(() => {
    if (remainingSeconds <= 0) {
      clearInterval(timeoutCounterId);
      void onConfirmation();
    }
  }, [remainingSeconds]);

  const handleProceed = () => {
    if (stateIsWarnAboutSubscription) {
      setStateIsWarnAboutSubscription(false);
    } else {
      setRemainingSeconds((remainingSeconds) => remainingSeconds - 1);
      const timeoutCounterId = setInterval(() => {
        setRemainingSeconds((remainingSeconds) => remainingSeconds - 1);
      }, DELETE_COUNTDOWN_INTERVAL_MS);
      setTimeoutCounterId(timeoutCounterId);
    }
  };

  const isManageLinkStep = stateIsWarnAboutSubscription && Boolean(manageLinkHref);
  const isManageMobileStep = stateIsWarnAboutSubscription && isMobileManagedSubscription && !manageLinkHref;
  const isManageStep = isManageLinkStep || isManageMobileStep;

  const primaryActionButtonProps = resolvePrimaryActionButtonProps({
    isManageLinkStep,
    isManageMobileStep,
    manageLinkHref,
    isExternalManageLink,
    onOpenPreventModal: handleOpenPreventModal,
    onCancel: handleCancel,
  });

  return (
    <>
      <Modal
        title={stateIsWarnAboutSubscription ? warningContent.TITLE : deletionContent.TITLE}
        open={isVisible}
        onOpenChange={handleCancel}
        dismissable={!isLoading}
      >
        <div className="text-start">{stateIsWarnAboutSubscription ? warningContent.DESCRIPTION : deletionContent.DESCRIPTION}</div>
        <Row className="gap-2">
          <SpicyButton variant="primary_large_bordered" color="transparent" isDisabled={isLoading} {...primaryActionButtonProps}>
            {isManageStep ? warningContent.CANCEL_BUTTON_TITLE : deletionContent.CANCEL_BUTTON_TITLE}
          </SpicyButton>
          <SpicyButton
            variant="primary_large"
            color="error_light"
            isLoading={isLoading}
            onPress={handleProceed}
            isDisabled={isLoading || shouldShowRemainingSeconds}
          >
            {stateIsWarnAboutSubscription ? warningContent.ACTION_BUTTON_TITLE : deletionContent.ACTION_BUTTON_TITLE}{" "}
            {shouldShowRemainingSeconds ? `(${remainingSeconds})` : ""}
          </SpicyButton>
        </Row>
      </Modal>
      <PreventManageSubscriptionModal open={stateIsPreventModalOpen} onClose={handleClosePreventModal} />
    </>
  );
}
